import CheckIcon from "@mui/icons-material/Check";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { AuthHeaderIconKey } from "./loginNoteIcons";
import { AuthSuccessContent } from "./loginSuccessUi";
import {
  getLoginDividerFooterSx,
  getLoginFieldSx,
  getLoginFooterLinkBoxSx,
  getLoginPrimaryButtonSx,
  getLoginSecondaryLinkSx,
  LoginFormHeader,
  loginFormPaddingSx,
} from "./loginUi";

type LoginStep = "sso" | "password";

export type LoginIdentityProvider = {
  id: string;
  label: string;
  onSelect: () => void;
  loading?: boolean;
};

export type LoginFormProps = {
  onSubmit?: (email: string, password: string) => void;
  identityProviders?: LoginIdentityProvider[];
  onClearError?: () => void;
  error?: string;
  loading?: boolean;
  success?: boolean;
  forgotPasswordUrl?: string;
  tenantName?: string;
  headerIcon?: AuthHeaderIconKey;
  translations: {
    welcomeBack: string;
    signInTitle: string;
    signInSubtitle: string;
    ssoSignInSubtitle: string;
    signIn: string;
    partnerOrExternalPrompt: string;
    signInWithEmailPassword: string;
    backToOrganizationLogin: string;
    email: string;
    password: string;
    emailPlaceholder: string;
    forgotPassword: string;
    fieldRequired: string;
    invalidEmail: string;
  };
};

export const LoginForm = ({
  onSubmit,
  identityProviders = [],
  onClearError,
  error,
  loading = false,
  success = false,
  forgotPasswordUrl = "/accounts/password_reset/",
  tenantName,
  headerIcon,
  translations: t,
}: LoginFormProps) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const hasIdentityProviders = identityProviders.length > 0;
  const [loginStep, setLoginStep] = useState<LoginStep>(() =>
    hasIdentityProviders ? "sso" : "password",
  );
  const identityProviderLoading = identityProviders.some(
    (provider) => provider.loading,
  );

  useEffect(() => {
    setLoginStep(hasIdentityProviders ? "sso" : "password");
  }, [hasIdentityProviders]);

  const showSsoStep = hasIdentityProviders && loginStep === "sso";
  const showPasswordStep = !hasIdentityProviders || loginStep === "password";
  const subtitle = showSsoStep ? t.ssoSignInSubtitle : t.signInSubtitle;

  const goToPasswordStep = () => {
    onClearError?.();
    setLoginStep("password");
  };

  const goToSsoStep = () => {
    onClearError?.();
    setLoginStep("sso");
  };

  const validate = (): boolean => {
    let valid = true;
    setEmailError("");
    setPasswordError("");

    if (!email.trim()) {
      setEmailError(t.fieldRequired);
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t.invalidEmail);
      valid = false;
    }

    if (!password) {
      setPasswordError(t.fieldRequired);
      valid = false;
    }

    return valid;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!showPasswordStep) {
      return;
    }
    if (validate()) {
      onSubmit?.(email.toLowerCase(), password);
    }
  };

  const fieldSx = getLoginFieldSx(theme);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{ ...loginFormPaddingSx, textAlign: "left" }}
      data-testid="login-form"
    >
      <AuthSuccessContent
        success={success}
        contentTestId="login-form-success-content"
        indicatorTestId="login-success-indicator"
      >
        <LoginFormHeader
          title={tenantName ? t.welcomeBack : t.signInTitle}
          subtitle={subtitle}
          headerIcon={headerIcon}
        />

        {error ? (
          <Alert severity="error" sx={{ mb: 2.5 }} data-testid="login-error">
            {error}
          </Alert>
        ) : null}

        {showSsoStep ? (
          <Box>
            {identityProviders.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || identityProviderLoading || success}
                onClick={() => provider.onSelect()}
                data-testid={`login-idp-${provider.id}`}
                sx={{ ...getLoginPrimaryButtonSx(theme), mb: 1.25 }}
              >
                {provider.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  provider.label
                )}
              </Button>
            ))}
            <Box sx={{ ...getLoginFooterLinkBoxSx(), textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.25 }}
                data-testid="login-partner-or-external-prompt"
              >
                {t.partnerOrExternalPrompt}
              </Typography>
              <Link
                component="button"
                type="button"
                underline="hover"
                color="text.secondary"
                sx={getLoginSecondaryLinkSx()}
                onClick={goToPasswordStep}
                data-testid="login-show-password-step"
              >
                {t.signInWithEmailPassword}
              </Link>
            </Box>
          </Box>
        ) : null}

        {showPasswordStep ? (
          <Box>
            <TextField
              fullWidth
              label={t.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              autoFocus
              sx={fieldSx}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { "data-testid": "login-email" },
                },
              }}
            />

            <TextField
              fullWidth
              label={t.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              autoComplete="current-password"
              sx={{ ...fieldSx, mb: 3 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  inputProps: { "data-testid": "login-password" },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || identityProviderLoading || success}
              data-testid="login-submit"
              sx={getLoginPrimaryButtonSx(theme)}
            >
              {success ? (
                <CheckIcon fontSize="small" />
              ) : loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t.signIn
              )}
            </Button>

            <Box sx={{ ...getLoginFooterLinkBoxSx(), textAlign: "center" }}>
              <Link
                href={forgotPasswordUrl}
                underline="hover"
                color="text.secondary"
                sx={getLoginSecondaryLinkSx()}
                data-testid="forgot-password-link"
              >
                {t.forgotPassword}
              </Link>
            </Box>

            {hasIdentityProviders ? (
              <Box sx={{ ...getLoginDividerFooterSx(), textAlign: "center" }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color="text.secondary"
                  sx={getLoginSecondaryLinkSx()}
                  onClick={goToSsoStep}
                  data-testid="login-back-to-sso"
                >
                  {t.backToOrganizationLogin}
                </Link>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </AuthSuccessContent>
    </Box>
  );
};
